import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Info,
  Loader2,
  CheckSquare,
  Download,
  Sparkles,
  ExternalLink,
  Library,
  Bookmark,
  MoreHorizontal,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  ArrowUp,
  Wrench,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Thumb } from "@/components/Thumb";
import { useChatStore } from "@/lib/stores/chatStore";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { splitMeta, formatWhen, STATUS_DOT } from "@/lib/format";
import { STATUS_META } from "@/components/StatusBadge";

const RichItemCard = ({ item, onOpen }) => {
  const { author } = splitMeta(item.meta);
  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid={`chat-item-card-${item.id}`}>
      <div className="p-3 flex gap-3">
        <Thumb platform={item.platform} size="row" className="w-24 h-16 rounded-md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px]">
            <span className="font-medium text-muted-foreground">{item.platform}</span>
            <span
              className={cn(
                "ml-auto flex items-center gap-1 font-medium",
                item.status === "pendiente" && "text-amber-700 dark:text-amber-400",
                item.status === "importante" && "text-teal-700 dark:text-teal-400",
                item.status === "aplicado" && "text-green-700 dark:text-green-400",
                (item.status === "inbox" || item.status === "archivado") && "text-muted-foreground",
              )}
            >
              <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT[item.status])} /> {STATUS_META[item.status]?.label}
            </span>
          </div>
          <p className="mt-0.5 font-medium text-[15px] leading-snug line-clamp-2">{item.title}</p>
          <p className="text-xs text-muted-foreground truncate">
            {author || item.mainTopic} · {splitMeta(item.meta).duration}
          </p>
        </div>
      </div>
      {item.summary && <p className="px-3 pb-2.5 text-sm text-muted-foreground line-clamp-2">{item.summary}</p>}
      <div className="px-3 pb-3 flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Ver en {item.platform}
          </a>
        </Button>
        <Button variant="outline" size="sm" onClick={onOpen}>
          <Library className="h-3.5 w-3.5" strokeWidth={1.75} /> Abrir en Biblioteca
        </Button>
        <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Guardar" onClick={() => toast("Guardado en tus destacados.")}>
          <Bookmark className="h-3.5 w-3.5" strokeWidth={1.75} />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 ml-auto" aria-label="Más" onClick={() => toast("Más acciones en la versión final.")}>
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
};

export default function Asistente() {
  const { messages, typing, send, lastResults, lastTools } = useChatStore();
  const { items, select, createAction, downloadItem } = useItemsStore();
  const [, navigate] = useLocation();
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const submit = (e) => {
    e.preventDefault();
    send(text);
    setText("");
  };

  const openItem = (id) => {
    select(id);
    navigate("/biblioteca");
  };

  const resultItems = lastResults.map((id) => items.find((i) => i.id === id)).filter(Boolean);

  return (
    <div className="h-full flex min-h-0 max-w-[1400px] mx-auto w-full md:px-8" data-testid="assistant-page">
      {/* Columna principal: chat */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="max-w-3xl w-full px-4 md:px-0 md:pr-6 py-5 space-y-4">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">Asistente</h1>
              <p className="mt-1 text-sm text-muted-foreground">Pregúntame sobre tu biblioteca. Solo consulto, no modifico nada.</p>
            </div>

            <div
              className="rounded-lg border border-primary/25 bg-accent text-accent-foreground px-3.5 py-2.5 flex items-start gap-2.5 text-sm"
              data-testid="assistant-banner"
            >
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-primary" strokeWidth={1.75} />
              <span className="flex-1">
                <span className="font-medium">Estás interactuando con un asistente de IA.</span> Puede equivocarse — revisa lo importante.
              </span>
              <button type="button" onClick={() => toast("Transparencia IA (AI Act, art. 50): los resúmenes y el asistente usan IA.")} className="text-primary text-[13px] font-medium hover:underline shrink-0">
                Más información
              </button>
            </div>

            <div className="space-y-6 pt-2" data-testid="chat-thread">
              {messages.map((m) => {
                const refItem = m.itemRefId ? items.find((i) => i.id === m.itemRefId) : null;
                if (m.role === "user")
                  return (
                    <div key={m.id} className="flex justify-end animate-fade-up">
                      <p className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-4 py-2 text-[15px]">
                        {m.text}
                      </p>
                    </div>
                  );
                return (
                  <div key={m.id} className="flex gap-2.5 animate-fade-up">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                      <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <div className="space-y-2.5 max-w-[88%] min-w-0 flex-1">
                      {m.toolCall && (
                        <span
                          className="inline-flex items-center gap-1.5 rounded-md border bg-secondary px-2.5 py-1 font-mono text-xs text-muted-foreground"
                          data-testid="tool-call-chip"
                        >
                          <Sparkles className="h-3 w-3 text-primary/70" strokeWidth={1.75} /> {m.toolCall}
                        </span>
                      )}
                      <p className="text-[15px] leading-relaxed">{m.text}</p>
                      {refItem && (
                        <>
                          <RichItemCard item={refItem} onOpen={() => openItem(refItem.id)} />
                          <p className="text-sm text-muted-foreground">
                            ¿Quieres que lo convierta en una tarea o que guarde una copia del vídeo?
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                createAction(refItem.id, "tarea");
                                toast.success("Tarea creada. La verás en Tareas.");
                              }}
                              data-testid="chat-create-task-button"
                            >
                              <CheckSquare className="h-3.5 w-3.5" strokeWidth={1.75} /> Crear tarea
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                downloadItem(refItem.id);
                                toast("Preparando la copia…", { description: "Descarga simulada en este prototipo." });
                              }}
                              data-testid="chat-save-copy-button"
                            >
                              <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> Guardar copia del vídeo
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => toast("Extracción de ideas en la versión final.")}>
                              <Lightbulb className="h-3.5 w-3.5" strokeWidth={1.75} /> Extraer ideas clave
                            </Button>
                            <span className="ml-auto flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Respuesta útil" onClick={() => toast("¡Gracias por el feedback!")}>
                                <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.75} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Respuesta no útil" onClick={() => toast("Gracias, lo tendré en cuenta.")}>
                                <ThumbsDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                              </Button>
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {typing && (
                <div className="flex gap-2.5" data-testid="chat-typing">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                    <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando en tu biblioteca…
                  </p>
                </div>
              )}
              <div ref={endRef} />
            </div>
          </div>
        </div>

        <div className="border-t bg-background shrink-0">
          <form onSubmit={submit} className="max-w-3xl w-full px-4 md:px-0 md:pr-6 py-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <button
                  type="button"
                  aria-label="Adjuntar"
                  onClick={() => toast("Adjuntos en la versión final.")}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <Paperclip className="h-4 w-4" strokeWidth={1.75} />
                </button>
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Pregunta cualquier cosa sobre tu biblioteca…"
                  className="pl-9"
                  data-testid="chat-input"
                />
              </div>
              <Button type="submit" size="icon" className="rounded-full shrink-0" aria-label="Enviar" data-testid="chat-send-button">
                <ArrowUp className="h-4 w-4" strokeWidth={2} />
              </Button>
            </div>
            <p className="mt-1.5 flex gap-3 text-[11px] text-muted-foreground">
              <span>
                <kbd className="rounded border bg-secondary px-1 py-px">Enter</kbd> para enviar
              </span>
              <span>
                <kbd className="rounded border bg-secondary px-1 py-px">↑↓</kbd> para navegar historial
              </span>
            </p>
          </form>
        </div>
      </div>

      {/* Raíl derecho: resultados + herramientas */}
      <aside className="hidden xl:flex w-[340px] border-l flex-col min-h-0 shrink-0">
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
          <section className="rounded-lg border bg-card">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold">Resultados ({resultItems.length})</h2>
              <button type="button" onClick={() => navigate("/biblioteca")} className="text-[13px] text-primary hover:underline">
                Ver en Biblioteca
              </button>
            </div>
            <div className="divide-y">
              {resultItems.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">Los contenidos que encuentre aparecerán aquí.</p>
              ) : (
                resultItems.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => openItem(i.id)}
                    className="w-full flex items-center gap-2.5 p-3 text-left hover:bg-secondary/60 transition-colors"
                  >
                    <Thumb platform={i.platform} size="mini" className="w-14 h-10 rounded-md" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium leading-snug line-clamp-2">{i.title}</span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        {i.platform} · {formatWhen(i.createdAt)}
                        <span className={cn("w-1.5 h-1.5 rounded-full ml-1", STATUS_DOT[i.status])} />
                        {STATUS_META[i.status]?.label}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className="rounded-lg border bg-card">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Wrench className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} /> Herramientas usadas
              </h2>
            </div>
            <div className="p-4 space-y-3">
              {lastTools.map((t, i) => (
                <div key={i} className="text-xs font-mono">
                  <p className="font-medium text-foreground">{t.name}</p>
                  <p className="mt-0.5 text-muted-foreground break-all">{t.args}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
